import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "portal", "hideOnEscape", "hideOnControl", "wrapperProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect } from 'react';
import { addGlobalEventListener } from 'ariakit-utils/events';
import { useSafeLayoutEffect, useBooleanEvent, useWrapElement } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useDisclosureContent } from '../disclosure/disclosure-content.js';
import { T as TooltipContext } from '../__utils-30a60887.js';
import { jsx } from 'react/jsx-runtime';
import { usePortal } from '../portal/portal.js';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltip({ state });
 * <TooltipAnchor state={state}>Anchor</TooltipAnchor>
 * <Role {...props}>Tooltip</Role>
 * ```
 */

var useTooltip = createHook(function (_ref) {
  var state = _ref.state,
      _ref$portal = _ref.portal,
      portal = _ref$portal === void 0 ? true : _ref$portal,
      _ref$hideOnEscape = _ref.hideOnEscape,
      hideOnEscape = _ref$hideOnEscape === void 0 ? true : _ref$hideOnEscape,
      _ref$hideOnControl = _ref.hideOnControl,
      hideOnControl = _ref$hideOnControl === void 0 ? false : _ref$hideOnControl,
      wrapperProps = _ref.wrapperProps,
      props = _objectWithoutProperties(_ref, _excluded);

  var popoverRef = state.popoverRef; // Makes sure the wrapper element that's passed to popper has the same
  // z-index as the popover element so users only need to set the z-index
  // once.

  useSafeLayoutEffect(function () {
    var wrapper = popoverRef.current;
    var tooltip = state.contentElement;
    if (!wrapper) return;
    if (!tooltip) return;
    wrapper.style.zIndex = getComputedStyle(tooltip).zIndex;
  }, [popoverRef, state.contentElement]);
  var hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  var hideOnControlProp = useBooleanEvent(hideOnControl); // Hide on Escape/Control

  useEffect(function () {
    if (!state.open) return;
    return addGlobalEventListener("keydown", function (event) {
      if (event.defaultPrevented) return;
      var isEscape = event.key === "Escape" && hideOnEscapeProp(event);
      var isControl = event.key === "Control" && hideOnControlProp(event);

      if (isEscape || isControl) {
        state.hide();
      }
    });
  }, [state.open, hideOnEscapeProp, hideOnControlProp, state.hide]);
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
      role: "presentation"
    }, wrapperProps), {}, {
      style: _objectSpread({
        position: state.fixed ? "fixed" : "absolute",
        top: 0,
        left: 0
      }, wrapperProps == null ? void 0 : wrapperProps.style),
      ref: popoverRef,
      children: element
    }));
  }, [state.fixed, popoverRef, wrapperProps]);
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(TooltipContext.Provider, {
      value: state,
      children: element
    });
  }, [state]);
  props = _objectSpread({
    role: "tooltip"
  }, props);
  props = useDisclosureContent(_objectSpread({
    state: state
  }, props));
  props = usePortal(_objectSpread(_objectSpread({
    portal: portal
  }, props), {}, {
    preserveTabOrder: false
  }));
  return props;
});
/**
 * A component that renders a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

var Tooltip = createComponent(function (props) {
  var htmlProps = useTooltip(props);
  return createElement("div", htmlProps);
});
export { Tooltip, useTooltip };